/**
 * Where each lesson sits on the winding path (mockup 03, [D-140](../../../docs/DECISIONS.md#d-140)).
 *
 * Pure, so the layout is tested without drawing it. Across as a percentage of
 * the width, so it fits a small phone and a tablet alike. Down in pixels,
 * because a lesson is the same height wherever it is read.
 */

/** Centre, left, centre, right — the swing the mockup draws. */
const SWING: readonly number[] = [50, 22, 50, 78];

/** From one node to the next, top to top. Room for a node, its label and the dots. */
export const ROW_PX = 118;
export const NODE_PX = 70;
/** The node to play next is drawn bigger, so the eye goes there first. */
export const NOW_PX = 82;
/** Room above the first node for the child standing on it. */
export const TOP_PX = 64;

export interface Placed {
  /** The node's centre, across, as a percentage of the width. */
  readonly x: number;
  /** The node's top, in pixels. */
  readonly y: number;
  readonly size: number;
}

export function windingLayout(states: readonly string[]): readonly Placed[] {
  return states.map((state, at) => ({
    x: SWING[at % SWING.length] ?? 50,
    y: TOP_PX + at * ROW_PX,
    size: state === 'now' ? NOW_PX : NODE_PX,
  }));
}

/** How tall the whole path is, so the page scrolls to the last label. */
export const windingHeight = (count: number): number =>
  count === 0 ? 0 : TOP_PX + (count - 1) * ROW_PX + NOW_PX + 48;

/**
 * The dots between two nodes: five, in the middle stretch of the line between
 * their centres, so none of them sits on a node.
 */
export function trail(from: Placed, to: Placed): readonly { x: number; y: number }[] {
  const y1 = from.y + from.size / 2;
  const y2 = to.y + to.size / 2;
  return [0, 1, 2, 3, 4].map((k) => {
    const f = 0.3 + k * 0.1;
    return { x: from.x + (to.x - from.x) * f, y: y1 + (y2 - y1) * f };
  });
}
